import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, FileText, Save, Plus, ChevronLeft, Loader2, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../lib/authFetch';
import { safeHtml } from '../lib/sanitize';
import { toast } from 'sonner';

export default function ContentManager() {
  const [view, setView] = useState('list'); // list, edit-reading, edit-lesson
  const [readings, setReadings] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const [readingsRes, lessonsRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/readings`),
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/lessons`),
      ]);
      if (readingsRes.ok) setReadings(await readingsRes.json());
      if (lessonsRes.ok) setLessons(await lessonsRes.json());
    } catch (err) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const handleEditReading = (reading) => {
    setEditItem({ ...reading, _type: 'reading' });
    setView('edit');
  };

  const handleEditLesson = (lesson) => {
    setEditItem({ ...lesson, _type: 'lesson' });
    setView('edit');
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const isReading = editItem._type === 'reading';
      const endpoint = isReading
        ? `/api/admin/content/readings/${editItem.reading_id}`
        : `/api/admin/content/lessons/${editItem.lesson_id}`;

      const body = isReading
        ? { title: editItem.title, description: editItem.description, content: editItem.content, estimated_time: editItem.estimated_time }
        : { title: editItem.title, subtitle: editItem.subtitle, content: editItem.content, estimated_time: editItem.estimated_time };

      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success('Content saved successfully!');
        await fetchContent();
        setView('list');
        setEditItem(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReading = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content/readings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'New Reading',
          description: 'Description here',
          week: readings.length + 1,
        }),
      });
      if (res.ok) {
        const newReading = await res.json();
        toast.success('Reading created');
        await fetchContent();
        handleEditReading(newReading);
      }
    } catch {
      toast.error('Failed to create reading');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  // Edit View
  if (view === 'edit' && editItem) {
    const isReading = editItem._type === 'reading';
    const wordCount = (editItem.content || '').replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => { setView('list'); setEditItem(null); }}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to list
          </button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
            data-testid="save-content-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </div>

        {/* Type badge */}
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${
            isReading ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
          }`}>
            {isReading
              ? `ARP ${editItem.week === 0 ? '— Introduction' : `— Week ${editItem.week}`}`
              : `Lesson ${editItem.lesson_id === 'intro' ? '— Introduction' : `— Week ${editItem.order || '?'}`}`
            }
          </span>
          <span className="text-xs text-zinc-400">
            {wordCount} words | ~{Math.ceil(wordCount / 200)} min read
          </span>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Title</label>
          <input
            type="text"
            value={editItem.title || ''}
            onChange={e => setEditItem({ ...editItem, title: e.target.value })}
            className="w-full mt-1 h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="content-title-input"
          />
        </div>

        {/* Subtitle (lessons only) */}
        {!isReading && (
          <div>
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Subtitle</label>
            <input
              type="text"
              value={editItem.subtitle || ''}
              onChange={e => setEditItem({ ...editItem, subtitle: e.target.value })}
              className="w-full mt-1 h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="content-subtitle-input"
            />
          </div>
        )}

        {/* Description (readings only) */}
        {isReading && (
          <div>
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Description</label>
            <input
              type="text"
              value={editItem.description || ''}
              onChange={e => setEditItem({ ...editItem, description: e.target.value })}
              className="w-full mt-1 h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="content-description-input"
            />
          </div>
        )}

        {/* Estimated time */}
        <div>
          <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Estimated Reading Time (minutes)</label>
          <input
            type="number"
            value={editItem.estimated_time || editItem.estimatedTime || 90}
            onChange={e => setEditItem({ ...editItem, estimated_time: parseInt(e.target.value) || 90 })}
            className="w-32 mt-1 h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="content-time-input"
          />
        </div>

        {/* Content Editor */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Content (HTML supported)</label>
            <span className="text-xs text-zinc-400">Users can listen to this via TTS</span>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2 text-xs text-amber-700">
            <strong>Formatting tips:</strong> Use &lt;h2&gt; for headings, &lt;p&gt; for paragraphs, &lt;ul&gt;&lt;li&gt; for lists, &lt;strong&gt; for bold, &lt;em&gt; for italic. Or just paste plain text: it will display as-is and TTS will read it aloud.
          </div>
          <textarea
            value={editItem.content || ''}
            onChange={e => setEditItem({ ...editItem, content: e.target.value })}
            rows={20}
            className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
            placeholder="Paste your content here. HTML tags are supported for formatting."
            data-testid="content-editor"
          />
        </div>

        {/* Preview */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <Eye className="w-3.5 h-3.5 text-zinc-400" />
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Preview</label>
          </div>
          <div
            className="bg-white border border-zinc-200 rounded-lg p-4 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={safeHtml(editItem.content || '<p>No content yet</p>')}
          />
        </div>

        {/* Bottom save */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="save-content-bottom-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save Changes
        </Button>
      </div>
    );
  }

  // List View — Organized by Weeks
  // Group readings and lessons by week number
  const getWeekNum = (item) => {
    if (item._isLesson) {
      if (item.lesson_id === 'intro') return 0;
      return item.order || 0;
    }
    return item.week ?? 0;
  };

  // Build week map
  const weekMap = {};
  readings.forEach(rd => {
    const w = rd.week ?? 0;
    if (!weekMap[w]) weekMap[w] = { reading: null, lesson: null };
    weekMap[w].reading = rd;
  });
  lessons.forEach(ls => {
    const w = ls.lesson_id === 'intro' ? 0 : (ls.order || 0);
    if (!weekMap[w]) weekMap[w] = { reading: null, lesson: null };
    weekMap[w].lesson = ls;
  });

  // Split into 2-week window (week 0 = intro) and course weeks (1-30)
  const windowWeeks = [0]; // Intro is the 2-week window material
  const courseWeeks = Object.keys(weekMap).map(Number).filter(w => w > 0).sort((a, b) => a - b);

  const hasContent = (item) => {
    if (!item) return false;
    const c = item.content || '';
    return c.length > 100 && !c.includes('coming soon') && !c.includes('Add your content');
  };

  const renderItem = (item, type) => {
    if (!item) return (
      <div className="text-xs text-zinc-300 italic px-2 py-1">No {type} assigned</div>
    );
    const published = hasContent(item);
    const isR = type === 'ARP';
    return (
      <button
        onClick={() => isR ? handleEditReading(item) : handleEditLesson(item)}
        className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors border ${
          isR ? 'border-blue-100 hover:border-blue-300' : 'border-purple-100 hover:border-purple-300'
        }`}
        data-testid={`edit-${isR ? 'reading' : 'lesson'}-${isR ? item.reading_id : item.lesson_id}`}
      >
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
          isR ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
        }`}>
          {type}
        </span>
        <span className="text-sm text-zinc-800 truncate flex-1">{item.title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
          published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {published ? 'Published' : 'Draft'}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-400">Content organised by week. Click any item to edit. Users hear readings/lessons via TTS.</p>

      {/* 2-Week Window Section */}
      <div>
        <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">2-Week Window (Assessment Period)</h4>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          {windowWeeks.map(w => (
            <div key={`window-${w}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-bold text-amber-800">Introduction</span>
              </div>
              <div className="space-y-1.5 pl-2">
                {renderItem(weekMap[w]?.reading, 'ARP')}
                {renderItem(weekMap[w]?.lesson, 'Lesson')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Course Weeks */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider">Programme Weeks</h4>
          <Button onClick={handleCreateReading} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="add-reading-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Reading
          </Button>
        </div>
        <div className="space-y-2">
          {courseWeeks.map(w => (
            <div key={`week-${w}`} className="bg-white border border-zinc-200 rounded-xl p-3" data-testid={`content-week-${w}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-xs font-bold">
                  Week {w}
                </span>
              </div>
              <div className="space-y-1.5 pl-2">
                {renderItem(weekMap[w]?.reading, 'ARP')}
                {renderItem(weekMap[w]?.lesson, 'Lesson')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
